import * as React from "react";

import { alpha, Paper, styled, Typography } from "@mui/material";
import * as Sentry from "@sentry/nextjs";

import { UserContext } from "context/usercontext";
import ErrorFallback from "src/common/components/ErrorFallback";
import Loader from "src/common/components/Loader";
import useBridgeClient from "src/common/hooks/BridgeSWRClient";
import { ActiveWorkflows, Notifications, WorkflowLauncher, useAllowLaunchWorkflow } from "src/domain/core/workflow";
import { endpoint } from "src/lib/bridgeClient";
import { useStyles } from "styles/global";

const Title = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
}));

const Heading = styled("div")(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
}));

const Container = styled("div")(() => ({
  display: "flex",
}));

const WorkflowContainer = styled("div")(() => ({
  width: "68%",
}));

const LauncherContainer = styled("div")(() => ({
  width: "32%",
  paddingLeft: "10px",
}));

const MaxStacks = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: "0 10px 0 10px",
  borderRadius: "15px",
  marginLeft: theme.spacing(2),
}));

const LimitReached = styled("div")(({ theme }) => ({
  ...theme.typography.body2,
  background: alpha(theme.palette.primary.main, 0.25),
  color: theme.palette.text.primary,
  padding: "0 10px 0 10px",
  borderRadius: "15px",
  marginLeft: theme.spacing(2),
}));

function WorkflowsErrorFallback() {
  return <ErrorFallback sx={{ margin: "100px 20px 0px 20px" }}>Unable to show active workflows.</ErrorFallback>;
}

function LauncherErrorFallback() {
  return <ErrorFallback sx={{ margin: "100px 20px 0px 20px" }}>Unable to show workflow launcher.</ErrorFallback>;
}

const HomePage = () => {
  const myUser = React.useContext(UserContext);

  const { data: workflows, error: workflowError } = useBridgeClient<Array<Workflow>>(endpoint.workflows, {
    refreshInterval: 15000,
  });

  const { data: instances, error: instancesError } = useBridgeClient<Nodes>(endpoint.myInstanceTypes);

  const workflowLaunchPermissions = useAllowLaunchWorkflow(workflows?.length, instances);

  const render = () => {
    if (workflowError) {
      return <div className="centered-disp">Unable to get active workflows</div>;
    }

    if (instancesError) {
      return <div className="centered-disp">Unable to get instances</div>;
    }

    if (!workflows || !instances) {
      return (
        <div className="centered-disp">
          <Loader />
        </div>
      );
    }

    return <ActiveWorkflows workflows={workflows} nodes={instances} />;
  };

  const classes = useStyles();
  return (
    <Container>
      <WorkflowContainer>
        <Sentry.ErrorBoundary fallback={WorkflowsErrorFallback} showDialog>
          <Heading sx={{ paddingLeft: "15px" }}>
            <Title>My Active Workflows</Title>
            <MaxStacks className={classes.styledPaper}>
              {workflows ? workflows.length : 0} of {myUser.max_workflows} Active
            </MaxStacks>
          </Heading>
          {render()}
        </Sentry.ErrorBoundary>
      </WorkflowContainer>
      <LauncherContainer>
        <Sentry.ErrorBoundary fallback={LauncherErrorFallback} showDialog>
          <Heading>
            <Title>Start a New Workflow</Title>
            {workflowLaunchPermissions.workflowLimitReached && <LimitReached>Limit Reached</LimitReached>}
          </Heading>
          <Notifications
            workflowCountUnknown={!!workflowError}
            userDisabled={workflowLaunchPermissions.userDisabled}
            workflowUsernameUnavailable={workflowLaunchPermissions.workflowUsernameUnavailable}
            keypairUnavailable={workflowLaunchPermissions.keypairUnavailable}
            missingNotifArn={workflowLaunchPermissions.missingNotifArn}
            invalidOrg={workflowLaunchPermissions.invalidOrg}
            missingIngressRules={workflowLaunchPermissions.missingIngressRules}
            noAvailableInstances={workflowLaunchPermissions.noAvailableInstances}
          />
          <WorkflowLauncher
            disabled={!(workflowLaunchPermissions.restricted === false && workflows && instances)}
            nodes={instances}
            instancesError={instancesError}
          />
        </Sentry.ErrorBoundary>
      </LauncherContainer>
    </Container>
  );
};

export default HomePage;
